import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AdminSidebar from '@/components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, CalendarCheck, Truck, UserPlus, DollarSign, Clock, TrendingUp, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/dashboard/stats`, getAuthHeader());
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [getAuthHeader]);

  const statCards = stats ? [
    { title: 'Total Customers', value: stats.total_customers, icon: Users, color: 'bg-blue-100 text-blue-600' },
    { title: 'Total Bookings', value: stats.total_bookings, icon: CalendarCheck, color: 'bg-emerald-100 text-emerald-600' },
    { title: 'Pending Bookings', value: stats.pending_bookings, icon: Clock, color: 'bg-amber-100 text-amber-600' },
    { title: 'Active Rentals', value: stats.active_bookings, icon: TrendingUp, color: 'bg-purple-100 text-purple-600' },
    { title: 'Total Vehicles', value: stats.total_vehicles, icon: Truck, color: 'bg-slate-100 text-slate-600' },
    { title: 'Available Vehicles', value: stats.available_vehicles, icon: Truck, color: 'bg-emerald-100 text-emerald-600' },
    { title: 'New Leads', value: stats.new_leads, icon: UserPlus, color: 'bg-blue-100 text-blue-600' },
    { title: 'Total Revenue', value: `$${stats.total_revenue.toLocaleString()}`, icon: DollarSign, color: 'bg-emerald-100 text-emerald-600' },
  ] : [];

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <AdminSidebar />
      
      <main className="flex-1 p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Welcome to the Fair Rideshare CRM</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-28 bg-white rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {statCards.map((stat, index) => (
                <Card key={index} className="stat-card" data-testid={`stat-card-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                        <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                      </div>
                      <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                        <stat.icon className="w-5 h-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Pending Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.pending_bookings > 0 && (
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <span className="text-slate-700">{stats.pending_bookings} booking(s) need approval</span>
                        <a href="/admin/bookings" className="text-blue-600 text-sm hover:underline">View</a>
                      </div>
                    )}
                    {stats.new_leads > 0 && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-slate-700">{stats.new_leads} new lead(s) to follow up</span>
                        <a href="/admin/leads" className="text-blue-600 text-sm hover:underline">View</a>
                      </div>
                    )}
                    {stats.pending_bookings === 0 && stats.new_leads === 0 && (
                      <p className="text-slate-500 text-center py-4">No pending actions</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Quick Links</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <a href="/admin/customers" className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-center">
                      <Users className="w-5 h-5 mx-auto mb-1 text-slate-600" />
                      <span className="text-sm text-slate-700">Customers</span>
                    </a>
                    <a href="/admin/bookings" className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-center">
                      <CalendarCheck className="w-5 h-5 mx-auto mb-1 text-slate-600" />
                      <span className="text-sm text-slate-700">Bookings</span>
                    </a>
                    <a href="/admin/vehicles" className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-center">
                      <Truck className="w-5 h-5 mx-auto mb-1 text-slate-600" />
                      <span className="text-sm text-slate-700">Vehicles</span>
                    </a>
                    <a href="/admin/leads" className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-center">
                      <UserPlus className="w-5 h-5 mx-auto mb-1 text-slate-600" />
                      <span className="text-sm text-slate-700">Leads</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
